import { getInstanceId } from '@va/dashboard/selectors/app';
import {
  addConsentForAiModel,
  AiChatbotConversation,
  AiChatbotConversationQuota,
  AiModel,
  deleteConversation,
  getAllConversations,
  getAvailableAiModels,
  getConversation,
  getConversationQuota,
  RawAiChatbotConversation,
  RawAiChatbotMessage,
  removeConsentForAiModel,
  sendMessage,
  startNewConversation,
  updateConversationMetadata,
} from '@va/dashboard/shared/ai-assistant';
import { useCurrentPeriodFilter } from '@va/dashboard/util-hooks';
import { useLocale } from '@va/localization';
import { isUserLoggedInWithCustomUrl } from '@va/standalone/shared/helpers';
import { is3asApp } from '@va/util/helpers';
import { useAsyncFunction, useFetchData } from '@va/util/hooks';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';

export const useAvailableAiModels = () => {
  const websiteId = useSelector(getInstanceId);

  const fetcher = useCallback(() => getAvailableAiModels(websiteId), [websiteId]);
  const mapper = useCallback(
    (response: Awaited<ReturnType<typeof getAvailableAiModels>>) =>
      response.models.map<AiModel>(({ consented, default: isDefault, model_name }) => ({
        consented,
        default: isDefault,
        name: model_name,
      })),
    [],
  );

  return useFetchData(['availableAiModels', websiteId], undefined, mapper, fetcher);
};

export const useEditConsentForAiModel = () => {
  const websiteId = useSelector(getInstanceId);

  const fn = useCallback(
    (modelName: string | string[], value: boolean) => {
      if (value) {
        return addConsentForAiModel(websiteId, modelName);
      }
      return removeConsentForAiModel(websiteId, modelName);
    },
    [websiteId],
  );

  return useAsyncFunction(fn);
};

export const useConversations = () => {
  const websiteId = useSelector(getInstanceId);

  const fetcher = useCallback(() => getAllConversations(websiteId), [websiteId]);
  const mapper = useCallback(
    (response: Awaited<ReturnType<typeof getAllConversations>>) =>
      response.conversations.map<AiChatbotConversation>(mapConversation),
    [],
  );

  return useFetchData(
    ['aiChatbotConversations', websiteId],
    {
      revalidateOnMount: true,
    },
    mapper,
    fetcher,
  );
};

export const useDeleteConversation = () => {
  const websiteId = useSelector(getInstanceId);

  const fn = useCallback((conversationId: string) => deleteConversation(websiteId, conversationId), [websiteId]);

  return useAsyncFunction(fn);
};

export const useUpdateConversationMetadata = () => {
  const websiteId = useSelector(getInstanceId);

  const fn = useCallback(
    (conversationId: string, data: { title?: string; selectedModel?: string }) =>
      updateConversationMetadata(websiteId, conversationId, data),
    [websiteId],
  );

  return useAsyncFunction(fn);
};

export const useStartNewConversation = () => {
  const websiteId = useSelector(getInstanceId);
  const { locale } = useLocale();
  const { from, until } = useCurrentPeriodFilter();

  const fn = useCallback(
    (message: string, model: string, abortController: AbortController) =>
      startNewConversation({
        websiteId,
        query: {
          from: from as string,
          until: until as string,
          generic: is3asApp() || isUserLoggedInWithCustomUrl(),
        },
        message,
        model,
        locale,
        abortController,
      }),
    [from, locale, until, websiteId],
  );

  return useAsyncFunction(fn);
};

export const useSendMessage = () => {
  const websiteId = useSelector(getInstanceId);
  const { locale } = useLocale();
  const { from, until } = useCurrentPeriodFilter();

  const fn = useCallback(
    (message: string, model: string, conversationId: string, abortController: AbortController) =>
      sendMessage({
        websiteId,
        query: {
          from: from as string,
          until: until as string,
          generic: is3asApp() || isUserLoggedInWithCustomUrl(),
        },
        conversationId,
        message,
        model,
        locale,
        abortController,
      }),
    [from, locale, until, websiteId],
  );

  return useAsyncFunction(fn);
};

export const useGetConversation = ({ conversationId }: { conversationId?: string }) => {
  const websiteId = useSelector(getInstanceId);

  const fetcher = useCallback(() => {
    if (!conversationId) return Promise.resolve({ conversation: {}, messageHistory: [] });
    return getConversation(websiteId, conversationId);
  }, [conversationId, websiteId]);
  const mapper = useCallback(
    ({ conversation, messageHistory }: Awaited<ReturnType<typeof getConversation>>) => ({
      conversation: mapConversation(conversation),
      messageHistory: messageHistory.map(mapMessage),
    }),
    [],
  );

  return useFetchData(
    ['getConversation', websiteId, conversationId],
    {
      revalidateOnMount: true,
    },
    mapper,
    fetcher,
  );
};

const mapConversation = (conversation: RawAiChatbotConversation) => ({
  createdAt: conversation.created_at,
  id: conversation.id,
  lastUpdatedAt: conversation.last_updated_at,
  selectedModel: conversation.selected_model,
  title: conversation.title,
  userId: conversation.user_id,
  websiteKey: conversation.website_key,
  questionsCount: conversation.num_questions,
});

export const mapMessage = (m: RawAiChatbotMessage) => ({
  content: m.content,
  conversationId: m.conversation_id,
  createdAt: m.created_at,
  id: m.id ?? crypto.randomUUID(),
  model: m.model,
  role: m.role,
  toolCalls: m.tool_calls,
  remoteId: m.remote_id,
});

export const useConversationQuota = (shouldFetch: boolean = true) => {
  const websiteId = useSelector(getInstanceId);

  const fetcher = useCallback(() => getConversationQuota(websiteId), [websiteId]);

  return useFetchData<AiChatbotConversationQuota>(
    shouldFetch ? ['aiChatbotConversationQuota', websiteId] : null,
    { revalidateOnMount: true },
    undefined,
    fetcher,
  );
};
